import { AutocompleteProps } from "@mui/material";
import { DesktopTimePickerProps } from "@mui/x-date-pickers";
import { ReactNode, useEffect, useState } from "react";
import CoreAutocomplete from "../select/Autocomplete";
import CoreTextField, { CoreTextFieldProps } from "../form/TextField";

export function changeTimeOnly(oldDateTime: Date, hours: number, minutes: number) {
	const newDateTime = new Date(oldDateTime.getTime());
	newDateTime.setHours(hours);
	newDateTime.setMinutes(minutes);
	newDateTime.setSeconds(0, 0);
	return newDateTime;
}

export function CoreTimePicker<AllowEmpty extends boolean = false>({
	defaultValue,
	value,
	allowEmpty,
	onChange,
	error,
	disabled,
	formatTime,
	ampm = true,
	disablePortal,
	placeholder = "HH:MM",
	disableOpenPicker,
	disabledTooltip,
	slotProps,
	fontSize,
	noneText,
	...autocompleteProps
}: {
	/** The default value. */
	defaultValue?: AllowEmpty extends true ? Date | null : Date;
	/** Controlled current value. */
	value?: AllowEmpty extends true ? Date | null : Date;
	/** Whether field can be left empty */
	allowEmpty?: AllowEmpty;
	/** Callback fired when the selected value changes. */
	onChange?: (d: AllowEmpty extends true ? Date | undefined : Date) => void;
	/** Controlled error state */
	error?: ReactNode;
	/** Formatter for time display */
	formatTime?: (d: Date) => string;
	/** 12h/24h view for hour selection clock. @default true */
	ampm?: boolean;
	/** The children will be under the DOM hierarchy of the parent component. @default false */
	disablePortal?: boolean;
	/** Placeholder text */
	placeholder?: string;
	/** Tooltip text when disabled */
	disabledTooltip?: ReactNode;
	/** Font size of the input field and dropdown menu */
	fontSize?: string;
	/** Text for the 'none' option */
	noneText?: AllowEmpty extends true ? string : undefined;
	/** Subcomponent props */
	slotProps?: {
		textField?: Partial<Omit<CoreTextFieldProps, "placeholder" | "error" | "onChange" | "onBlur" | "disabledTooltip">>;
	};
} & Pick<DesktopTimePickerProps<Date>, "timezone" | "disabled" | "disableOpenPicker"> &
	Pick<
		AutocompleteProps<
			AllowEmpty extends true ? Date | null : Date,
			false,
			AllowEmpty extends true ? false : true,
			true
		>,
		"className" | "sx"
	>) {
	type DataType = AllowEmpty extends true ? Date | null : Date;
	const baseDate = value ?? defaultValue ?? new Date();

	const [inputValue, setInputValue] = useState("");
	const [formatError, setFormatError] = useState(false);
	useEffect(() => {
		try {
			if (!allowEmpty || inputValue.trim()) {
				parseFreeInput(inputValue.trim());
			}
			setFormatError(false);
		} catch (_) {
			setFormatError(true);
		}
	}, [inputValue]);

	const getFormattedTime = (time?: Date) => {
		if (!time) {
			return "";
		}
		if (formatTime) {
			return formatTime(time);
		}
		const hour = time.getHours();
		const minute = time.getMinutes();
		const minuteString = String(minute).padStart(2, "0");
		if (ampm) {
			const part = hour > 11 ? "P.M." : "A.M.";
			return `${String(hour > 12 ? hour - 12 : hour).padStart(2, "0")}:${minuteString} ${part}`;
		} else {
			return `${String(hour).padStart(2, "0")}:${minuteString}`;
		}
	};

	useEffect(() => {
		try {
			const parsedValue = allowEmpty && !inputValue.trim() ? null : parseFreeInput(inputValue.trim());
			if (value !== parsedValue) {
				setInputValue(getFormattedTime(value ?? undefined));
			}
		} catch {
			setInputValue(getFormattedTime(value ?? undefined));
		}
	}, [value]);

	const parseFreeInput = (input: string) => {
		const matches = /^(\d{1,2})(?:\s*:\s*(\d{2}))?\s*([ap]\.?m\.?)?$/i.exec(input);
		if (!matches) {
			throw new Error("Invalid format");
		}
		const [, hoursString, minutesString, part] = matches;
		let hours = Number(hoursString);
		const minutes = minutesString ? Number(minutesString) : 0;
		if (part?.toLowerCase().startsWith("p")) {
			if (hours < 12) {
				hours += 12;
			}
		}
		if (isNaN(hours) || hours > 23 || isNaN(minutes) || minutes > 59) {
			throw new Error("Invalid time");
		}
		return changeTimeOnly(baseDate, hours, minutes);
	};

	const onDateChange = (newValue?: Date) => {
		if (newValue || allowEmpty) {
			onChange?.(newValue as AllowEmpty extends true ? Date | undefined : Date);
			setInputValue(getFormattedTime(newValue));
		}
	};

	const onValueChange = (_event: unknown, newValue: Date | string | (Date | string)[] | null) => {
		if (typeof newValue === "string") {
			if (!newValue.trim()) {
				onDateChange(undefined);
			} else {
				try {
					onDateChange(parseFreeInput(newValue.trim()));
				} catch (e) {
					console.error(e);
					const oldValue = value ?? defaultValue;
					onDateChange(oldValue ?? undefined);
				}
			}
		} else if (newValue instanceof Date) {
			onDateChange(newValue);
		} else if (!newValue) {
			onDateChange(undefined);
		}
	};

	const options: DataType[] = Array.from({ length: 24 }, (_, i) => changeTimeOnly(baseDate, i, 0) as DataType);
	if (allowEmpty) {
		options.splice(0, 0, null as DataType);
	}

	return (
		<CoreAutocomplete
			disableClearable
			{...({
				...autocompleteProps,
				value,
				defaultValue,
			} as Partial<AutocompleteProps<DataType, false, true, true>>)}
			freeSolo
			options={disableOpenPicker ? [] : options}
			onChange={onValueChange}
			inputValue={inputValue}
			onInputChange={(_, text) => setInputValue(text)}
			disabled={disabled}
			getOptionLabel={(value) =>
				typeof value === "string" ? value : value == null ? (noneText ?? "None") : getFormattedTime(value)
			}
			renderInput={(props) => (
				<CoreTextField
					placeholder={placeholder}
					disabledTooltip={disabledTooltip}
					{...props}
					error={error || formatError}
					{...slotProps?.textField}
					onBlur={() => {
						if (!formatError) {
							onValueChange(undefined, inputValue);
						}
					}}
					sx={
						fontSize
							? {
									"& .MuiInputBase-root": {
										fontSize,
									},
								}
							: undefined
					}
				/>
			)}
			disablePortal={disablePortal}
			fontSize={fontSize}
		/>
	);
}
